import "./RoutingConditionDrawer.css";
import { useState } from "react";
import { Drawer } from "@/components/Drawer";
import {
  type AutoAdvanceCondition,
  summarizeCondition,
} from "./RoutingConditionSummary";

type ConditionKind =
  | "none"
  | "always"
  | "classification_confidence_above"
  | "all_fields_confidence_above"
  | "field_equals"
  | "qc_sample";

interface OperatorOverrideRow {
  userId: string;
  rate: string;
}

export interface RoutingConditionDrawerProps {
  isOpen: boolean;
  stepLabel: string;
  condition: AutoAdvanceCondition | null;
  fields: Array<{ internalName: string; label: string }>;
  onSave: (condition: AutoAdvanceCondition | null) => void;
  onClose: () => void;
}

/** Clamp a numeric-string form value into [min, max], preserving "" and rejecting NaN. */
function clampRange(raw: string, min: number, max: number): string {
  if (raw === "") return "";
  const n = Number(raw);
  if (Number.isNaN(n)) return "";
  if (n < min) return String(min);
  if (n > max) return String(max);
  return raw;
}

const clamp01 = (raw: string) => clampRange(raw, 0, 1);
/** E-020 QC sampling — 0..100 percentage. */
const clampPercent = (raw: string) => clampRange(raw, 0, 100);

function initialKind(c: AutoAdvanceCondition | null): ConditionKind {
  if (!c) return "none";
  return c.type;
}

/**
 * RoutingConditionDrawer — typed builder for a pipeline step's
 * autoAdvanceCondition. WU-3 / R-93.
 *
 * CONTRACT: form state is seeded from the `condition` prop at mount
 * only (useState initial values). Callers MUST mount a fresh instance
 * per step — e.g. `<RoutingConditionDrawer key={step.id} ... />` — so
 * switching the edited step resets the form. Changing the `condition`
 * prop on an already-mounted instance will NOT update the form.
 */
export function RoutingConditionDrawer({
  isOpen,
  stepLabel,
  condition,
  fields,
  onSave,
  onClose,
}: RoutingConditionDrawerProps) {
  const [kind, setKind] = useState<ConditionKind>(initialKind(condition));
  const [classThreshold, setClassThreshold] = useState(
    condition?.type === "classification_confidence_above"
      ? String(condition.threshold)
      : "",
  );
  const [fieldsThreshold, setFieldsThreshold] = useState(
    condition?.type === "all_fields_confidence_above"
      ? String(condition.threshold)
      : "",
  );
  const [eqField, setEqField] = useState(
    condition?.type === "field_equals" ? condition.field : "",
  );
  const [eqValue, setEqValue] = useState(
    condition?.type === "field_equals" ? condition.value : "",
  );
  // E-020 — QC sampling. Default hold rate 10% (a sensible spot-check).
  const [qcPercent, setQcPercent] = useState(
    condition?.type === "qc_sample" ? String(condition.samplePercent) : "10",
  );
  const [operatorOverrides, setOperatorOverrides] = useState<OperatorOverrideRow[]>(
    condition?.type === "qc_sample" && condition.operatorSampleRates
      ? Object.entries(condition.operatorSampleRates).map(([userId, rate]) => ({
          userId,
          rate: String(rate),
        }))
      : [],
  );

  // Tri-state: a condition object = valid; `null` = "no condition"
  // (PATCH clears autoAdvanceCondition); `undefined` = form incomplete
  // (Save stays disabled).
  function build(): AutoAdvanceCondition | null | undefined {
    switch (kind) {
      case "none":
        return null;
      case "always":
        return { type: "always" };
      case "classification_confidence_above":
        if (classThreshold === "") return undefined;
        return {
          type: "classification_confidence_above",
          threshold: Number(classThreshold),
        };
      case "all_fields_confidence_above":
        if (fieldsThreshold === "") return undefined;
        return {
          type: "all_fields_confidence_above",
          threshold: Number(fieldsThreshold),
        };
      case "field_equals":
        if (!eqField || !eqValue) return undefined;
        return { type: "field_equals", field: eqField, value: eqValue };
      case "qc_sample": {
        if (qcPercent === "") return undefined;
        // Only keep complete override rows (userId + numeric rate).
        const entries = operatorOverrides
          .filter((r) => r.userId.trim() !== "" && r.rate !== "")
          .map((r) => [r.userId.trim(), Number(r.rate)] as const);
        const base = {
          type: "qc_sample" as const,
          samplePercent: Number(qcPercent),
        };
        return entries.length > 0
          ? { ...base, operatorSampleRates: Object.fromEntries(entries) }
          : base;
      }
    }
  }

  const built = build();
  const canSave = built !== undefined;

  const footer = (
    <>
      <button type="button" className="btn" onClick={onClose}>
        Cancel
      </button>
      <button
        type="button"
        className="btn btn--primary"
        disabled={!canSave}
        onClick={() => {
          if (built !== undefined) onSave(built);
        }}
      >
        Save condition
      </button>
    </>
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Routing condition"
      width="sm"
      footer={footer}
    >
      <p className="routing-drawer__step">
        Step: <strong>{stepLabel}</strong>
      </p>
      <fieldset className="routing-drawer__fieldset">
        <legend>When this is met, skip this step:</legend>

        <div className="routing-drawer__row">
          <input
            type="radio"
            id="rc-none"
            name="cond-kind"
            checked={kind === "none"}
            onChange={() => setKind("none")}
          />
          <label htmlFor="rc-none">No condition (always run)</label>
        </div>

        <div className="routing-drawer__row">
          <input
            type="radio"
            id="rc-always"
            name="cond-kind"
            checked={kind === "always"}
            onChange={() => setKind("always")}
          />
          <label htmlFor="rc-always">Always (skip every time)</label>
        </div>

        <div className="routing-drawer__row">
          <input
            type="radio"
            id="rc-class"
            name="cond-kind"
            checked={kind === "classification_confidence_above"}
            onChange={() => setKind("classification_confidence_above")}
          />
          <label htmlFor="rc-class">Classification confidence ≥</label>
          <input
            type="number"
            data-testid="threshold-classification"
            aria-label="Minimum classification score"
            min={0}
            max={1}
            step={0.01}
            value={classThreshold}
            onChange={(e) => setClassThreshold(clamp01(e.target.value))}
            onFocus={() => setKind("classification_confidence_above")}
          />
        </div>

        <div className="routing-drawer__row">
          <input
            type="radio"
            id="rc-fields"
            name="cond-kind"
            checked={kind === "all_fields_confidence_above"}
            onChange={() => setKind("all_fields_confidence_above")}
          />
          <label htmlFor="rc-fields">All field confidences ≥</label>
          <input
            type="number"
            data-testid="threshold-fields"
            aria-label="Minimum field score"
            min={0}
            max={1}
            step={0.01}
            value={fieldsThreshold}
            onChange={(e) => setFieldsThreshold(clamp01(e.target.value))}
            onFocus={() => setKind("all_fields_confidence_above")}
          />
        </div>

        <div className="routing-drawer__row">
          <input
            type="radio"
            id="rc-fieldeq"
            name="cond-kind"
            checked={kind === "field_equals"}
            onChange={() => setKind("field_equals")}
          />
          <label htmlFor="rc-fieldeq">Field equals</label>
          <select
            data-testid="field-equals-field"
            aria-label="Which field to compare"
            value={eqField}
            onChange={(e) => setEqField(e.target.value)}
            onFocus={() => setKind("field_equals")}
          >
            <option value="">— select —</option>
            {fields.map((f) => (
              <option key={f.internalName} value={f.internalName}>
                {f.label}
              </option>
            ))}
          </select>
          <input
            type="text"
            data-testid="field-equals-value"
            aria-label="Value to match"
            value={eqValue}
            onChange={(e) => setEqValue(e.target.value)}
            onFocus={() => setKind("field_equals")}
          />
        </div>

        {/* E-020 — QC sampling: hold a % of docs for QC, auto-advance the rest. */}
        <div className="routing-drawer__row">
          <input
            type="radio"
            id="rc-qc-sample"
            name="cond-kind"
            checked={kind === "qc_sample"}
            onChange={() => setKind("qc_sample")}
          />
          <label htmlFor="rc-qc-sample">Hold % for QC (auto-advance the rest)</label>
          <input
            type="number"
            data-testid="qc-sample-percent"
            aria-label="Percent of documents to hold for QC"
            min={0}
            max={100}
            step={1}
            value={qcPercent}
            onChange={(e) => setQcPercent(clampPercent(e.target.value))}
            onFocus={() => setKind("qc_sample")}
          />
        </div>

        {kind === "qc_sample" && (
          <div className="routing-drawer__qc-overrides">
            <div className="routing-drawer__qc-overrides-head">
              Per-operator hold rates (optional) — override the rate for a specific
              operator (e.g. hold more of a trainee&apos;s docs).
            </div>
            {operatorOverrides.map((row, i) => (
              <div className="routing-drawer__row" key={i}>
                <input
                  type="text"
                  data-testid={`qc-operator-id-${i}`}
                  aria-label={`Operator user id, override ${i + 1}`}
                  placeholder="operator user id"
                  value={row.userId}
                  onChange={(e) =>
                    setOperatorOverrides((rows) =>
                      rows.map((r, j) =>
                        j === i ? { ...r, userId: e.target.value } : r,
                      ),
                    )
                  }
                />
                <input
                  type="number"
                  data-testid={`qc-operator-rate-${i}`}
                  aria-label={`Hold percent for operator override ${i + 1}`}
                  min={0}
                  max={100}
                  step={1}
                  value={row.rate}
                  onChange={(e) =>
                    setOperatorOverrides((rows) =>
                      rows.map((r, j) =>
                        j === i
                          ? { ...r, rate: clampPercent(e.target.value) }
                          : r,
                      ),
                    )
                  }
                />
                <button
                  type="button"
                  className="btn btn--sm"
                  data-testid={`qc-remove-operator-override-${i}`}
                  aria-label={`Remove operator override ${i + 1}`}
                  onClick={() =>
                    setOperatorOverrides((rows) => rows.filter((_, j) => j !== i))
                  }
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn btn--sm"
              data-testid="qc-add-operator-override"
              onClick={() =>
                setOperatorOverrides((rows) => [...rows, { userId: "", rate: "" }])
              }
            >
              + Add operator override
            </button>
          </div>
        )}
      </fieldset>

      <div className="routing-drawer__preview">
        Preview:{" "}
        <em>
          {built === undefined
            ? "(complete the fields above)"
            : summarizeCondition(built)}
        </em>
      </div>
    </Drawer>
  );
}
